import { memo } from 'react';
import { Download, EllipsisVertical, Folder, Paperclip, Trash2 } from 'lucide-react';
import Badge from '../Badge.jsx';
import Dropdown from '../Dropdown.jsx';

const normalizeDescriptionText = (value, maxLength = 180) => {
  if (typeof value !== 'string') return '';

  const plainText = value
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[[^\]]*]\([^)]+\)/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\r?\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!plainText) return '';
  if (plainText.length <= maxLength) return plainText;
  return `${plainText.slice(0, maxLength - 3).trimEnd()}...`;
};

const ChatInputDocumentSearchCard = ({ doc, onMenuAction, onDeleteClick, onCardClick }) => {
  const title = doc?.title ?? '';
  const agentName = doc?.agentName ?? '';
  const driveName = doc?.driveName ?? '';
  const description = normalizeDescriptionText(doc?.description ?? '');
  const hasDriveName = driveName.trim() !== '';
  const driveMenuItemLabel = hasDriveName ? 'Go to drive' : 'Save to drive';
  const driveMenuItemValue = hasDriveName ? 'goToDrive' : 'saveToDrive';

  return (
    <div className="group flex min-h-[105px] w-full items-stretch rounded-lg px-3 py-2 hover:bg-surface-hover-subtle">
      <button
        type="button"
        onClick={onCardClick ? (event) => onCardClick(doc, event) : undefined}
        className={[
          'flex min-w-0 flex-1 flex-col gap-2 text-left',
          onCardClick ? 'cursor-pointer' : 'cursor-default',
        ].join(' ')}
      >
        <div className="truncate text-sm font-semibold text-content">{title}</div>
        <div className="flex flex-wrap items-center gap-3">
          {agentName ? (
            <Badge variant="purple" size="small" className="!h-5 !text-[11px] !font-medium">
              {agentName}
            </Badge>
          ) : null}
          {driveName ? (
            <Badge variant="lightBlue" size="small" className="!h-5 !text-[11px] !font-medium">
              {driveName}
            </Badge>
          ) : null}
        </div>
        <div className="w-[90%] min-w-0">
          <p className="line-clamp-2 text-sm text-content-secondary">{description}</p>
        </div>
      </button>

      <div className="invisible flex shrink-0 flex-col items-center justify-center gap-2 group-hover:visible">
        <Dropdown
          trigger={(
            <button
              type="button"
              className="inline-flex p-2 text-content-secondary hover:text-content"
              aria-label="Menu"
            >
              <EllipsisVertical size={16} />
            </button>
          )}
          menuPosition="top"
          alignMenu="left"
          usePortal
          menuClassName="z-[2000] min-w-[180px]"
        >
          <Dropdown.Item
            icon={Folder}
            onClick={() => onMenuAction?.(driveMenuItemValue, doc)}
          >
            {driveMenuItemLabel}
          </Dropdown.Item>
          <Dropdown.Item icon={Paperclip} onClick={() => onMenuAction?.('attachToChat', doc)}>
            Attach to chat
          </Dropdown.Item>
          <Dropdown.Item icon={Download} onClick={() => onMenuAction?.('download', doc)}>
            Download
          </Dropdown.Item>
        </Dropdown>
        <button
          type="button"
          onClick={() => onDeleteClick?.(doc)}
          aria-label="Delete"
          className="inline-flex p-2 text-content-secondary hover:text-content"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default memo(ChatInputDocumentSearchCard);
